import React, { useState, useEffect, useRef, useCallback } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import SettingsSection from './SettingsSection'
import RemovableList from './RemovableList'
import SelectInput from './SelectInput'
import TextInput, { OptionalTextInput } from './TextInput'
import LetterInput, { isLetter } from './LetterInput'
import PhoneInput, { isPhoneNumber } from './PhoneInput'
import { addToCallChain, useDebouncedChainedCallback } from './CallCenter'
import {
    apiGetAdminConfig, apiSetAlias, apiRemoveAlias, apiAddAccountToProject, apiRemoveAccountFromProject,
    apiSetSwishScalars, apiSetNotificationConfig, apiSetCardLastFour, apiClearCardLastFour, apiSetForeignPayFromAccount,
    apiSetZettleCardFeeAccount, apiSetZettleCashAccount, apiSetSwishFee, apiSetIntakeRoles,
    AdminConfigData, AdminChartAccount, AdminProject, AccountKind,
} from './Api'

// Account aliases: an admin-curated friendly name over Visma's account name (used everywhere
// the chart is shown). Add-or-change is an upsert, so re-picking an account overwrites it.
function AliasEditor({ data, reload }: { data: AdminConfigData, reload: () => void }) {
    const [account, setAccount] = useState<number | null>(null)
    const [name, setName] = useState('')
    const vismaName = (n: number) => data.accounts.find(a => a.number === n)?.name ?? ''

    const save = () => {
        if (account === null || !name.trim()) return
        apiSetAlias(account, name.trim()).then(() => { setAccount(null); setName(''); reload() })
    }

    return (
        <SettingsSection title="Kontonamn (alias)" description="Eget visningsnamn ovanpå Vismas kontonamn.">
            <RemovableList items={data.aliases} getKey={alias => alias.account_number} emptyText="Inga än."
                renderItem={alias => <span>{alias.account_number} · {vismaName(alias.account_number)} → <strong>{alias.display_name}</strong></span>}
                onRemove={alias => apiRemoveAlias(alias.account_number).then(reload)} />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <SelectInput label="Konto" required={true} sx={{ minWidth: 260 }}
                    options={data.accounts} valueGetter={a => a.number} nameGetter={a => `${a.number} · ${a.name}`}
                    value={account} onChange={setAccount}/>
                <TextInput label="Visningsnamn" value={name} onChange={setName}
                    onKeyDown={e => { if (e.key === 'Enter') save() }} />
                <Button size="small" variant="outlined" disabled={account === null || !name.trim()} onClick={save}>Spara</Button>
            </Box>
        </SettingsSection>
    )
}

// Which accounts belong to a project (rel_account_project) — the chart map that decides which
// accounts the cost-centre pickers offer for that project.
//
// Every project at once, rather than one chosen from a dropdown. The map is only interesting in
// aggregate: the question people bring here is which projects can take income, or expenses, and
// answering it used to mean picking each project in turn and remembering what the last one said.
//
// It matters because a project with no account of a given kind is dropped from that kind's picker
// entirely -- api_utilities masks it out before scope filtering even runs, once for income and once
// for expenses -- so the gap is invisible in the view where it does damage. Showing every project's
// two sides side by side is the whole feature; there is deliberately no filter over it, since which
// side a project needs is something only the reader knows.
const KIND_ORDER = ['income', 'expense', 'balance'] as const
const KIND_HEADING: Record<AccountKind, string> = {
    income: 'Intäkt', expense: 'Utgift', balance: 'Balans',
}

function AccountProjectRow({ data, project, accounts, expanded, onToggle, reload }: {
    data: AdminConfigData, project: AdminProject, accounts: AdminChartAccount[],
    expanded: boolean, onToggle: () => void, reload: () => void,
}) {
    const assigned = accounts.map(a => a.number)
    const byKind = KIND_ORDER
        .map(kind => ({ kind, numbers: accounts.filter(a => a.type === kind).map(a => a.number) }))
        .filter(group => group.numbers.length > 0)
    const missingIncome = !accounts.some(a => a.type === 'income')

    return (
        <Box sx={{ borderTop: '1px solid #eee', px: 1, py: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap',
                       cursor: 'pointer' }}
                 onClick={onToggle}>
                <Box sx={{ flex: '0 0 14rem', fontWeight: 500 }}>
                    {project.number} · {project.name}
                </Box>
                <Box sx={{ flex: 1, fontSize: '0.9em', color: '#555',
                           display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {byKind.map(group => (
                        <span key={group.kind}>
                            <strong style={{ fontWeight: 500 }}>{KIND_HEADING[group.kind]}</strong>{' '}
                            {group.numbers.join(', ')}
                        </span>
                    ))}
                    {/* Named rather than merely absent: an empty cell reads as "not loaded yet". */}
                    {missingIncome && <span style={{ color: '#b26a00' }}>saknar intäktskonto</span>}
                </Box>
                {/* The whole row is clickable, but the button carries the handler too so the
                    control is reachable by keyboard; stopPropagation keeps it from toggling twice. */}
                <Button size="small" onClick={e => { e.stopPropagation(); onToggle() }}>
                    {expanded ? 'Stäng' : 'Ändra'}
                </Button>
            </Box>
            {expanded && (
                <Box sx={{ pl: 2, pb: 1 }}>
                    <ProjectAccounts data={data} project={project} assigned={assigned}
                        reload={reload}/>
                </Box>
            )}
        </Box>
    )
}

/**
 * Which accounts a project has, as one list to tick through.
 *
 * A project's accounts are decided in one sitting — someone works down the chart deciding what this
 * project may book to — and the old shape made that one dialog per account: pick, save, wait for
 * the page to reload, pick the next. The whole set is now in front of the reader with a box beside
 * each account, which is also the only way to see what is *not* ticked.
 *
 * Each tick is saved on its own, as it always was. What is deferred is refetching the page: doing
 * that per tick moved the list under the pointer mid-run, and there is nothing to refetch *for*
 * until the reader is done, so it happens when the row closes.
 */
function ProjectAccounts({ data, project, assigned, reload }: {
    data: AdminConfigData, project: AdminProject, assigned: number[], reload: () => void,
}) {
    const [selected, setSelected] = useState<number[]>(assigned)
    const changed = useRef(false)
    useEffect(() => () => { if (changed.current) reload() }, [])

    // Grouped, and therefore sorted by group: MUI's groupBy renders a heading each time the group
    // changes as it walks the list, so unsorted options produce the same heading several times.
    const options = [...data.accounts].sort((a, b) =>
        KIND_ORDER.indexOf(a.type) - KIND_ORDER.indexOf(b.type) || a.number - b.number)
    const chosen = options.filter(a => selected.includes(a.number))

    const apply = (next: AdminChartAccount[]) => {
        const wanted = next.map(a => a.number)
        const added = wanted.filter(n => !selected.includes(n))
        const removed = selected.filter(n => !wanted.includes(n))
        const before = selected
        setSelected(wanted)
        changed.current = true
        // Not awaited, and the control is not disabled meanwhile: ticking is done in runs, and a
        // field that goes dead between ticks closes its own list. Each account is its own request,
        // so they cannot conflict; a failure puts the box back and is reported on the error bus.
        void Promise.all([
            ...added.map(n => apiAddAccountToProject(n, project.id)),
            ...removed.map(n => apiRemoveAccountFromProject(n, project.id)),
        ]).catch(() => setSelected(before))
    }

    return (
        <Autocomplete<AdminChartAccount, true, false, false>
            multiple disableCloseOnSelect
            options={options} value={chosen}
            groupBy={a => KIND_HEADING[a.type]}
            getOptionLabel={a => `${a.number} · ${a.name}`}
            isOptionEqualToValue={(a, b) => a.number === b.number}
            onChange={(_event, next) => apply(next)}
            renderOption={(props, option, { selected: ticked }) => {
                const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string }
                return (
                    <li key={key} {...rest}>
                        <Checkbox size="small" checked={ticked} sx={{ mr: 1, p: 0.5 }}/>
                        {option.number} · {option.name}
                    </li>
                )
            }}
            renderInput={params => <TextField {...params} label="Konton" placeholder="Sök konto…"/>}
            sx={{ maxWidth: '40rem', mt: 0.5 }}/>
    )
}

function AccountProjectEditor({ data, reload }: { data: AdminConfigData, reload: () => void }) {
    const [expandedId, setExpandedId] = useState<string | null>(null)

    // project id -> its accounts, in chart order.
    const accountsOf = new Map<string, AdminChartAccount[]>()
    for (const link of data.account_project) {
        const account = data.accounts.find(a => a.number === link.account_number)
        if (account === undefined) continue     // mapped to an account no longer in Visma's chart
        accountsOf.set(link.project_id, [...(accountsOf.get(link.project_id) ?? []), account])
    }

    const projects = [...data.projects].sort((a, b) => a.number.localeCompare(b.number, 'sv'))

    return (
        <SettingsSection title="Konton per projekt"
                         description="Bestämmer vilka konton som går att välja för ett projekt.">
            <Box sx={{ mt: 0.5 }}>
                {projects.map(project => (
                    <AccountProjectRow key={project.id} data={data} project={project}
                        accounts={accountsOf.get(project.id) ?? []}
                        expanded={expandedId === project.id}
                        onToggle={() => setExpandedId(id => id === project.id ? null : project.id)}
                        reload={reload}/>
                ))}
            </Box>
        </SettingsSection>
    )
}

// The Swish bookkeeping scalars (formerly swish.json): the connected bank account, the clearing
// account, the voucher series letters and the poster payee number. Account fields are Visma
// dropdowns; edits auto-save on a debounce (like expense drafts), so there's no Spara button.
function SwishScalarsEditor({ data }: { data: AdminConfigData }) {
    const s = data.swish_scalars
    const [bankAccount, setBankAccount] = useState<number | null>(s?.bank_account ?? null)
    const [clearingAccount, setClearingAccount] = useState<number | null>(s?.clearing_account ?? null)
    const [clearingSeries, setClearingSeries] = useState(s?.clearing_voucher_series ?? '')
    const [recognitionSeries, setRecognitionSeries] = useState(s?.recognition_voucher_series ?? '')
    const [swishNumber, setSwishNumber] = useState(s?.swish_number ?? '')
    const changedRef = useRef(false)
    const edited = () => { changedRef.current = true }

    // Timed auto-save (like expense drafts, CallCenter.useDebouncedChainedCallback): persist a
    // debounced snapshot once the required fields are set. Incomplete edits (a cleared account or a
    // half-typed series) simply aren't saved — the DB columns are NOT NULL. The hook flushes any
    // pending save when the component unmounts (leaving the tab), so nothing is lost.
    const debouncedSave = useDebouncedChainedCallback(async () => {
        if (bankAccount === null || clearingAccount === null || !isLetter(clearingSeries)
            || !(recognitionSeries === '' || isLetter(recognitionSeries))
            || !(swishNumber === '' || isPhoneNumber(swishNumber))) return
        await apiSetSwishScalars({
            bank_account: bankAccount, clearing_account: clearingAccount,
            clearing_voucher_series: clearingSeries,
            recognition_voucher_series: recognitionSeries || null,
            swish_number: swishNumber || null,
        })
    }, 1500)
    useEffect(() => {
        if (changedRef.current) { changedRef.current = false; debouncedSave() }
    })

    // Two sections, one row and one save: the bank account and the redovisning series are shared by
    // all three rails (the card payout lands on the same account, and the card and kassa vouchers use
    // the same series), the clearing and the poster number are Swish's own.
    return (<>
        <SettingsSection title="Swish och Zettle"
                         description="Bankkontot där Swish-insättningar och Zettles utbetalningar kommer in, och serien som Swish-, kort- och kassaredovisningarna bokförs i.">
            <Box sx={{ display: 'flex', flexFlow: 'column nowrap', gap: 1.5, maxWidth: 420 }}>
                <SelectInput label="Bankkonto" required={true} sx={{ minWidth: 260 }}
                    options={data.bank_accounts} valueGetter={a => a.number} nameGetter={a => `${a.number} · ${a.name}`}
                    value={bankAccount} onChange={v => { setBankAccount(v); edited() }}/>
                <LetterInput label="Redovisningsserie (tom = Vismas standard)" optional value={recognitionSeries}
                    onChange={v => { setRecognitionSeries(v); edited() }}/>
            </Box>
        </SettingsSection>
        <SettingsSection title="Swish"
                         description="Varje Swish-insättning får ett eget clearingverifikat i clearingserien. Swish-numret är det affischernas QR-koder betalar till.">
            <Box sx={{ display: 'flex', flexFlow: 'column nowrap', gap: 1.5, maxWidth: 420 }}>
                <SelectInput label="Clearingkonto" required={true} sx={{ minWidth: 260 }}
                    options={data.accounts} valueGetter={a => a.number} nameGetter={a => `${a.number} · ${a.name}`}
                    value={clearingAccount} onChange={v => { setClearingAccount(v); edited() }}/>
                <LetterInput label="Clearingserie (en bokstav)" value={clearingSeries}
                    onChange={v => { setClearingSeries(v); edited() }}/>
                <PhoneInput label="Swish-nummer (för affischer)" optional value={swishNumber}
                    onChange={v => { setSwishNumber(v); edited() }}/>
            </Box>
        </SettingsSection>
    </>)
}

// The bank's Swish fee. Charged per received payment but drawn as one monthly lump, so the Swish
// recognition voucher books it as a cost of the scopes whose payments incurred it and owes it on the
// skuldkonto until the lump arrives (docs/zettle-swish.md §13). Only expense accounts are offered for
// the cost and BAS class 2 for the skuld. The accounts save on pick, the amount when the field is left.
function SwishFeeEditor({ data }: { data: AdminConfigData }) {
    const fee = data.swish_fee
    const [account, setAccount] = useState<number | null>(fee.account)
    const [liability, setLiability] = useState<number | null>(fee.liability_account)
    const [amount, setAmount] = useState(
        fee.ore_per_payment ? (fee.ore_per_payment / 100).toFixed(2).replace('.', ',') : '')
    const [error, setError] = useState<string | null>(null)
    const expenseAccounts = data.accounts.filter(a => a.type === 'expense')
    const liabilityAccounts = data.accounts.filter(a => a.number >= 2000 && a.number <= 2999)
    const needsAccounts = amount.trim() !== ''   // the fee is optional; its accounts only once it is set
    const saveAmount = () => {
        const text = amount.trim().replace(',', '.')
        const ore = text === '' ? null : Math.round(Number(text) * 100)
        if (ore !== null && !(Number.isFinite(ore) && ore >= 0)) {
            setError('Ange ett belopp i kronor, t.ex. 2,00')
            return
        }
        setError(null)
        addToCallChain(async () => { await apiSetSwishFee({ ore_per_payment: ore }) }, true)
    }

    return (
        <SettingsSection title="Swish-avgift"
                         description="Bankens avgift per mottagen Swish-betalning. Eko kostnadsför den per kostnadsställe och projekt och skuldför den tills banken drar månadens avgifter. Lämna tomt om den inte ska bokföras.">
            <Box sx={{ display: 'flex', flexFlow: 'column nowrap', gap: 1.5, maxWidth: 420 }}>
                <div>
                    <OptionalTextInput id="swish-fee-amount" label="Avgift per betalning (kr)" sx={{ maxWidth: 260 }}
                        value={amount} placeholder="t.ex. 2,00"
                        inputProps={{ inputMode: 'decimal' }} onChange={setAmount} onBlur={saveAmount}/>
                    {error && <Typography variant="caption" color="error">{error}</Typography>}
                </div>
                <SelectInput label="Kostnadskonto" required={needsAccounts} sx={{ minWidth: 260 }}
                    options={expenseAccounts} valueGetter={a => a.number} nameGetter={a => `${a.number} · ${a.name}`}
                    value={account} onChange={v => {
                        setAccount(v)
                        if (v !== null) addToCallChain(async () => { await apiSetSwishFee({ account: v }) }, true)
                    }}/>
                <SelectInput label="Skuldkonto" required={needsAccounts} sx={{ minWidth: 260 }}
                    options={liabilityAccounts} valueGetter={a => a.number} nameGetter={a => `${a.number} · ${a.name}`}
                    value={liability} onChange={v => {
                        setLiability(v)
                        if (v !== null) addToCallChain(async () => { await apiSetSwishFee({ liability_account: v }) }, true)
                    }}/>
            </Box>
        </SettingsSection>
    )
}

// The Zettle bookkeeping accounts. The card fee: Zettle keeps it before paying out, so what lands
// in the bank is the net and the fee is booked separately — only expense accounts are offered. The
// cash account: kontant becomes the club's the moment it is handed over, so the till's takings are
// debited here on the sales day — only BAS group 19 (kassa och bank) is offered. Both save on pick,
// like the foreign pay-from account.
function ZettleScalarsEditor({ data }: { data: AdminConfigData }) {
    const [feeAccount, setFeeAccount] = useState<number | null>(data.zettle_card_fee_account)
    const [cashAccount, setCashAccount] = useState<number | null>(data.zettle_cash_account)
    const expenseAccounts = data.accounts.filter(a => a.type === 'expense')
    const cashAccounts = data.accounts.filter(a => a.number >= 1900 && a.number <= 1999)

    return (
        <SettingsSection title="Kort och kassa"
                         description="Zettle drar kortavgiften innan utbetalningen. Kontanter bokförs på kassakontot den dag de tas emot.">
            <Box sx={{ display: 'flex', flexFlow: 'column nowrap', gap: 1.5, maxWidth: 420 }}>
                <SelectInput label="Kortavgifter" required={true} sx={{ minWidth: 260 }}
                    options={expenseAccounts} valueGetter={a => a.number} nameGetter={a => `${a.number} · ${a.name}`}
                    value={feeAccount} onChange={v => {
                        setFeeAccount(v)
                        if (v !== null) addToCallChain(async () => { await apiSetZettleCardFeeAccount(v) }, true)
                    }}/>
                <SelectInput label="Kassa" required={true} sx={{ minWidth: 260 }}
                    options={cashAccounts} valueGetter={a => a.number} nameGetter={a => `${a.number} · ${a.name}`}
                    value={cashAccount} onChange={v => {
                        setCashAccount(v)
                        if (v !== null) addToCallChain(async () => { await apiSetZettleCashAccount(v) }, true)
                    }}/>
            </Box>
        </SettingsSection>
    )
}

// Which of the club's own accounts foreign supplier invoices are paid from. Visma requires it
// before any foreign supplier can be created: an utlandsbetalning names the debit account
// explicitly, where a domestic giro payment leaves it implicit in the bankgiro agreement. Saved
// immediately on pick — it's a single choice, so there's nothing to debounce.
function ForeignPaymentEditor({ data }: { data: AdminConfigData }) {
    const [account, setAccount] = useState<number | null>(data.foreign_pay_from_account)

    return (
        <SettingsSection title="Utländska leverantörsfakturor"
                         description="Kontot som utländska leverantörsfakturor betalas från. Måste väljas innan en utländsk leverantör kan skapas. (Skatteverkets betalningskod väljs per leverantör — den säger vad just den betalningen avser.)">
            <Box sx={{ display: 'flex', flexFlow: 'column nowrap', gap: 1.5, maxWidth: 420 }}>
                <SelectInput label="Betala från" required={true} sx={{ minWidth: 260 }}
                    options={data.bank_accounts} valueGetter={a => a.number} nameGetter={a => `${a.number} · ${a.name}`}
                    value={account} onChange={v => {
                        setAccount(v)
                        if (v !== null) addToCallChain(async () => { await apiSetForeignPayFromAccount(v) }, true)
                    }}/>
            </Box>
        </SettingsSection>
    )
}

// The last four digits of the club's own card, which is how Eko tells a purchase the club has
// already paid for from one a member is owed for. It was a constant in the source until now: a fact
// about one club's one card, invisible to the people it describes and wrong the moment the card is
// reissued.
//
// Saved on a debounce like the other typed fields. Empty means unknown, and unknown is deliberately
// not the same as "no card" — Eko then declines to guess who paid rather than assuming the member
// did, because that assumption is the one that costs money.
function CardEditor({ data }: { data: AdminConfigData }) {
    const [lastFour, setLastFour] = useState(data.card_last_four ?? '')
    const [error, setError] = useState<string | null>(null)
    const changedRef = useRef(false)

    const debouncedSave = useDebouncedChainedCallback(async () => {
        if (lastFour === '') {
            setError(null)
            await apiClearCardLastFour()
        } else if (/^\d{4}$/.test(lastFour)) {
            setError(null)
            await apiSetCardLastFour(lastFour)
        } else {
            setError('Ange exakt fyra siffror.')
        }
    }, 1500)
    useEffect(() => {
        if (changedRef.current) { changedRef.current = false; debouncedSave() }
    })

    return (
        <SettingsSection title="Föreningens kort"
                         description={<>
                             De fyra sista siffrorna i föreningens eget betalkort. Eko läser dem på kvittot för
                             att avgöra om föreningen redan har betalat — annars behandlas köpet som ett utlägg
                             som ska ersättas. Lämna tomt om föreningen inte har något kort; då gissar Eko inte,
                             och inga kvitton skickas till texttolkningen. Ändringar sparas automatiskt.
                         </>}>
            <Box sx={{ display: 'flex', flexFlow: 'column nowrap', gap: 1.5, maxWidth: 420 }}>
                <TextField size="small" label="Kortets fyra sista siffror" sx={{ maxWidth: 260 }}
                    value={lastFour} error={error !== null} helperText={error ?? ' '}
                    inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                    onChange={e => {
                        setLastFour(e.target.value.replace(/\D/g, '').slice(0, 4))
                        changedRef.current = true
                    }}/>
            </Box>
        </SettingsSection>
    )
}

// Which roles see underlag that nobody holds yet. A fresh upload has no rows, so no scope says whose
// it is: a club of many sub-treasurers wants all of them to see it, a club with one treasurer wants
// only that role to. None ticked means every role that may attest, as Eko always behaved. Each tick
// saves the whole choice at once.
function IntakeRolesEditor({ data }: { data: AdminConfigData }) {
    const [chosen, setChosen] = useState<Set<number>>(new Set(data.intake_role_ids))
    const toggle = (roleId: number) => {
        const next = new Set(chosen)
        if (next.has(roleId)) next.delete(roleId)
        else next.add(roleId)
        setChosen(next)
        addToCallChain(async () => { await apiSetIntakeRoles([...next]) }, true)
    }
    return (
        <SettingsSection title="Nya underlag"
                         description="Underlag som ingen ännu ansvarar för visas i Attestera för de roller som är valda här. Övriga roller ser bara det som lämnats till dem. Väljs ingen ser alla som får attestera nya underlag.">
            <Box sx={{ display: 'flex', flexFlow: 'column nowrap' }}>
                {data.attesting_roles.map(role =>
                    <FormControlLabel key={role.id} label={role.name}
                        control={<Checkbox checked={chosen.has(role.id)} onChange={() => toggle(role.id)}/>}/>)}
            </Box>
        </SettingsSection>
    )
}

// Attest-notification policy (a single row). Auto-saved on a debounce, like the Swish scalars. The
// nightly job is also a no-op unless SMTP is configured on the server, so enabling here is
// necessary but not sufficient.
function NotificationsEditor({ data }: { data: AdminConfigData }) {
    const n = data.notifications
    const [enabled, setEnabled] = useState(n?.enabled ?? false)
    const [reminder, setReminder] = useState(n?.reminder_interval_days ?? 3)
    const [dueSoon, setDueSoon] = useState(n?.due_soon_days ?? 5)
    const changedRef = useRef(false)
    const edited = () => { changedRef.current = true }

    const debouncedSave = useDebouncedChainedCallback(async () => {
        await apiSetNotificationConfig({ enabled, reminder_interval_days: reminder, due_soon_days: dueSoon })
    }, 1500)
    useEffect(() => {
        if (changedRef.current) { changedRef.current = false; debouncedSave() }
    })

    return (
        <SettingsSection title="Attest-notiser"
                         description={<>
                             Skickar en daglig e-postpåminnelse till den som är ansvarig för en post. Kräver att
                             e-postutskick är konfigurerat på servern. Ändringar sparas automatiskt.
                         </>}>
            <Box sx={{ display: 'flex', flexFlow: 'column nowrap', gap: 1.5, maxWidth: 420 }}>
                <FormControlLabel
                    // ml: 0 cancels MUI's default -11px, which otherwise makes the switch hang left
                    // of the outlined TextFields below it (see AllScopesToggle for the same fix).
                    sx={{ ml: 0 }}
                    control={<Switch checked={enabled} onChange={e => { setEnabled(e.target.checked); edited() }} />}
                    label="Aktivera notiser" />
                <TextField type="number" size="small" label="Påminn var (dagar)" sx={{ maxWidth: 260 }}
                    value={reminder} inputProps={{ min: 1 }}
                    onChange={e => { setReminder(Math.max(1, parseInt(e.target.value) || 1)); edited() }} />
                <TextField type="number" size="small" label="Brådskande om förfaller inom (dagar)" sx={{ maxWidth: 260 }}
                    value={dueSoon} inputProps={{ min: 0 }}
                    onChange={e => { setDueSoon(Math.max(0, parseInt(e.target.value) || 0)); edited() }} />
            </Box>
        </SettingsSection>
    )
}

export default function Config() {
    const [data, setData] = useState<AdminConfigData | null>(null)
    const reload = useCallback(() => { apiGetAdminConfig().then(setData).catch(() => {}) }, [])
    useEffect(() => { reload() }, [reload])

    if (data === null) return null
    return (
        <Box sx={{ p: 2, maxWidth: 900 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>Företag</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Föreningsövergripande inställningar för kontoplanen. Ändringar sparas direkt.
            </Typography>
            <SwishScalarsEditor data={data} />
            <SwishFeeEditor data={data} />
            <ZettleScalarsEditor data={data} />
            <ForeignPaymentEditor data={data} />
            <CardEditor data={data} />
            <IntakeRolesEditor data={data} />
            <NotificationsEditor data={data} />
            <AliasEditor data={data} reload={reload} />
            <AccountProjectEditor data={data} reload={reload} />
        </Box>
    )
}
